import {
  type INavigationClient,
  NavigationClient,
  type NavigationOptions,
} from '@azure/msal-browser';

export const MOCK_REDIRECT_PATH = '/mock-auth/redirect.html';

const isMockRedirectPath = (pathname: string): boolean => {
  return pathname === MOCK_REDIRECT_PATH;
};

type RedirectCallback = (
  outcome: string,
  username: string | null,
  state: string | null,
) => void;

export interface IUnitTestNavigationClient extends INavigationClient {
  setCallback(cb: RedirectCallback): void;
}

// For unit tests: intercepts mock-redirect navigations and fires the callback
// synchronously, allowing redirect flows to complete without a real browser navigation.
export class UnitTestNavigationClient implements IUnitTestNavigationClient {
  private onRedirect: RedirectCallback | null = null;

  setCallback(cb: RedirectCallback): void {
    this.onRedirect = cb;
  }

  private toBase64Url(value: string): string {
    return Buffer.from(value, 'utf-8')
      .toString('base64')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '');
  }

  private encodeAuthorizationCode(
    username: string | null,
    nonce: string | null,
  ): string {
    return `mock_code.${this.toBase64Url(JSON.stringify({ username, nonce }))}`;
  }

  private async handleMockRedirect(url: string): Promise<boolean> {
    const parsed = new URL(url, globalThis.location.origin);
    if (!isMockRedirectPath(parsed.pathname)) {
      return false;
    }

    const outcome = parsed.searchParams.get('mock_outcome') ?? 'success';
    const username =
      parsed.searchParams.get('login_hint') ??
      parsed.searchParams.get('username');
    const state = parsed.searchParams.get('state');
    this.onRedirect?.(outcome, username, state);

    const redirectUri =
      parsed.searchParams.get('redirect_uri') ??
      parsed.searchParams.get('redirecturl');
    if (!redirectUri || !state) {
      return true;
    }
    const callbackUrl = new URL(redirectUri, globalThis.location.origin);
    if (outcome === 'success') {
      const nonce = parsed.searchParams.get('nonce');
      const code = this.encodeAuthorizationCode(username, nonce);
      callbackUrl.hash = `code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    } else {
      callbackUrl.hash = `error=access_denied&error_description=${encodeURIComponent('MockMsal redirect failure')}&state=${encodeURIComponent(state)}`;
    }
    globalThis.history.replaceState(
      null,
      '',
      `${callbackUrl.pathname}${callbackUrl.search}${callbackUrl.hash}`,
    );
    return true;
  }

  async navigateExternal(
    url: string,
    _options: NavigationOptions,
  ): Promise<boolean> {
    await this.handleMockRedirect(url);
    return false;
  }

  async navigateInternal(
    url: string,
    _options: NavigationOptions,
  ): Promise<boolean> {
    await this.handleMockRedirect(url);
    return false;
  }
}

export class MockNavigationClient extends NavigationClient {
  public readonly navigations: string[] = [];

  override async navigateInternal(
    url: string,
    _options: NavigationOptions,
  ): Promise<boolean> {
    this.navigations.push(url);
    return false;
  }

  override async navigateExternal(
    url: string,
    _options: NavigationOptions,
  ): Promise<boolean> {
    this.navigations.push(url);
    return false;
  }
}
